/**
 * Product Hub money handling.
 *
 * ## The problem this file exists to solve
 *
 * Every money column in the rest of this repo is `DOUBLE` (`Payments.amount`,
 * `SubscriptionPlanItems.price`, …), and `node-postgres` hands `double precision` back as a
 * JavaScript `number`. Product Hub stores money as `DECIMAL(12,2)` instead, because a price list
 * that shows the difference between a base price and a store price cannot afford binary floating
 * point — `189 * 1.1` renders as `207.90000000000003`.
 *
 * The cost of that choice is a trap: **`node-postgres` returns `numeric` as a string.**
 * `StorePrice.price` arrives as `"249.00"`, not `249`. Nothing throws; `"249.00" + 10` quietly
 * becomes `"249.0010"` and a wrong number reaches the customer.
 *
 * So: models declare these columns as `string | number`, and every service serializer runs the
 * value through `toMoney` before it leaves the service layer. The API contract is always `number`.
 *
 * A global `pg.types.setTypeParser(1700, parseFloat)` would also fix it and is deliberately **not**
 * used — it would silently change the behaviour of every future `numeric` column in the codebase,
 * including ones added by someone who never read this comment.
 */
import { roundMoney } from './payment.types';

/**
 * `roundMoney` is imported rather than re-implemented so there is exactly one rounding rule in the
 * monorepo; it is intentionally not re-exported, because `libs/types/src/index.ts` re-exports both
 * files with `export *` and a second export of the same name would be ambiguous. Product Hub code
 * should reach for the operations below, which round for it.
 */

/** A money value as it may arrive from Sequelize: a DECIMAL string, a number, or nothing. */
export type MoneyInput = string | number | null | undefined;

/**
 * Normalise a money value coming out of the database into a number.
 *
 * Returns `null` for null/undefined/empty — "no price" is a real state in this module and must not
 * collapse into `0`, which would read as "free". A non-numeric string also returns `null` rather
 * than `NaN`, so a bad row degrades to "missing" instead of poisoning every sum downstream.
 */
export const toMoney = (value: MoneyInput): number | null => {
  if (value === null || value === undefined) return null;
  if (typeof value === 'number') return Number.isFinite(value) ? roundMoney(value) : null;

  const trimmed = value.trim();
  if (trimmed === '') return null;

  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? roundMoney(parsed) : null;
};

/** `toMoney` for the callers that genuinely want 0 rather than null (totals, counters). */
export const toMoneyOrZero = (value: MoneyInput): number => toMoney(value) ?? 0;

/**
 * `toMoney`'s sibling for DECIMAL columns that are **not** money — dimensions and weights.
 *
 * Same string-from-Postgres trap, different rounding: a length is `DECIMAL(10,3)`, so forcing it
 * through the 2-decimal money rounding would quietly turn 12.345 cm into 12.35 cm. This keeps
 * the value as-is and only fixes the type.
 */
export const toNumeric = (value: MoneyInput): number | null => {
  if (value === null || value === undefined) return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;

  const trimmed = value.trim();
  if (trimmed === '') return null;

  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
};

/** Add two money values, rounding the result. Nulls count as zero. */
export const addMoney = (a: MoneyInput, b: MoneyInput): number => roundMoney(toMoneyOrZero(a) + toMoneyOrZero(b));

/** Subtract `b` from `a`, rounding the result. Nulls count as zero. */
export const subtractMoney = (a: MoneyInput, b: MoneyInput): number => roundMoney(toMoneyOrZero(a) - toMoneyOrZero(b));

/** Multiply a money value by a plain factor (quantity, 1 + percentage…), rounding the result. */
export const multiplyMoney = (value: MoneyInput, factor: number): number =>
  roundMoney(toMoneyOrZero(value) * (Number.isFinite(factor) ? factor : 0));

/** Sum a list of money values, rounding once per addition so the total matches the displayed lines. */
export const sumMoney = (values: MoneyInput[]): number => values.reduce<number>((total, v) => addMoney(total, v), 0);

/**
 * Apply a percentage to a money value.
 *
 * `percent` is the human number — `10` means 10%, not `0.1`. Used by both the bulk price
 * operations and the percentage promotions, so the two can never drift apart.
 */
export const applyPercent = (value: MoneyInput, percent: number): number =>
  multiplyMoney(value, 1 + (Number.isFinite(percent) ? percent : 0) / 100);

/** The share `percent` describes, e.g. the discount amount rather than the discounted price. */
export const percentOf = (value: MoneyInput, percent: number): number =>
  multiplyMoney(value, (Number.isFinite(percent) ? percent : 0) / 100);

/**
 * The string form to write back into a DECIMAL column.
 *
 * Sequelize accepts a number, but handing it a fixed-precision string keeps the value the database
 * stores byte-identical to the value that was rounded here, with no second float round-trip.
 */
export const toMoneyColumn = (value: MoneyInput): string | null => {
  const n = toMoney(value);
  return n === null ? null : n.toFixed(2);
};
